import React, { useImperativeHandle, useState } from 'react';
import { useHilContext, useHilReader, useHilWriter, useHilSubmit, useHilStore } from '@bizfirst/hil-react';
import { HilActionBar } from '@bizfirst/hil-ui';
import type { HilPresentationAction } from '@bizfirst/hil-types';
import { HilPresentationType, HilDecision } from '@bizfirst/hil-types';
import { HilChatPresetRow } from './HilChatPresetRow';
import { HilChatTextInput } from './HilChatTextInput';
import type { HilChatMessage, HilChatMessageStatus } from '../types/HilChatMessage';
import type { HilChatWindowSettings } from '../settings/HilChatWindowSettings';

interface HilChatComposerProps {
  settings: HilChatWindowSettings;
  currentStep: number;
  /** Filled values for the current step's form bubble (owned by HilChatWindow, updated via HilChatFormMessage's onFormValuesChange). Irrelevant for chat-kind steps. */
  currentFormData: Record<string, unknown>;
  onAdvance: (nextStep: number) => void;
  onMessageSent: (message: HilChatMessage) => void;
  /** Also called with 'pending' when a retry resets a previously failed message, not just the terminal 'sent'/'failed' from a normal send. */
  onMessageStatusChange: (id: string, status: HilChatMessageStatus) => void;
  nextEchoID: () => string;
}

export interface HilChatComposerHandle {
  /** Re-attempts the send for a previously failed echoed message, reusing its id. No-ops if the message no longer belongs to the current step. */
  retry: (message: HilChatMessage) => void;
}

/**
 * Reimplements HilPresentationRenderer's private advanceOrSubmit orchestration using the
 * same reusable low-level primitives (writer.addFormStep/addChatStep/addApprovalStep,
 * setDecision, setActionName, setCurrentUserAsResponder, useHilSubmit().submit()) — that
 * orchestration function itself is not exported from hil-ui, so it cannot be imported
 * wholesale. Exposes an imperative `retry` handle rather than a reactive prop so a parent
 * can trigger a re-send from a user click without introducing an effect after this
 * component's hooks (all hooks here must run unconditionally, before any null-guarded render).
 */
export const HilChatComposer = React.forwardRef<HilChatComposerHandle, HilChatComposerProps>(function HilChatComposer(
  { settings, currentStep, currentFormData, onAdvance, onMessageSent, onMessageStatusChange, nextEchoID },
  ref
) {
  const reader = useHilReader();
  const writer = useHilWriter();
  const { submit, submitting, error } = useHilSubmit();
  const { currentUserID } = useHilContext();
  const [draft, setDraft] = useState('');

  const step = reader?.forStep(currentStep) ?? null;
  const allActions = reader && step ? reader.getActions(currentStep) : [];
  const actions = allActions.filter(a => a.Style !== 'preset');
  const presetActions = allActions.filter(a => a.Style === 'preset');
  const presentations = reader ? reader.getPresentations() : [];
  const isLastStep = currentStep === presentations[presentations.length - 1]?.Order;
  const isChatStep = step?.presentationType() === HilPresentationType.Chat;
  const isApprovalStep = step?.presentationType() === HilPresentationType.Approval;
  const disabled = submitting;

  const send = async (action: HilPresentationAction, replyText: string, retryID?: string) => {
    if (!reader || !writer || !step) return;
    if (isChatStep && !replyText.trim()) return;

    const echoID = retryID ?? nextEchoID();
    if (retryID) {
      // Re-attempt: reuse the existing transcript entry rather than appending a duplicate.
      onMessageStatusChange(retryID, 'pending');
    } else {
      onMessageSent({
        id: echoID,
        kind: 'text',
        sender: 'user',
        order: currentStep,
        status: 'pending',
        sentAt: new Date().toISOString(),
        promptMessage: isChatStep || isApprovalStep ? (replyText || null) : 'Form submitted',
        formID: null,
        formSchemaJson: null,
        prePopulate: {},
        actions: [],
        presetActions: [],
      });
    }

    if (isChatStep) {
      writer.addChatStep({ stepOrder: currentStep, replyText, actionName: action.ActionKey });
    } else if (isApprovalStep) {
      writer.addApprovalStep({ stepOrder: currentStep, comment: replyText || undefined, actionName: action.ActionKey });
    } else {
      writer.addFormStep({ stepOrder: currentStep, data: currentFormData, actionName: action.ActionKey });
    }

    const decision = action.Decision ?? HilDecision.Submitted;
    setDraft('');

    if (isLastStep || decision !== '') {
      writer
        .setDecision(decision)
        .setActionName(action.ActionKey)
        .setCurrentUserAsResponder(currentUserID ?? reader.firstActorID() ?? '');

      // useHilSubmit().submit() owns engine.resolve()/store resolution on success but
      // doesn't return an ok/fail result to the caller — read the store directly right
      // after the await for the synchronous status this echo entry needs.
      //
      // Deliberately NOT guarded by an isMountedRef-style check: for a re-suspending chat
      // node (Octopus AI Agent), BaseHilRespondController awaits the full Phase-2 turn
      // (LLM call, tool calls, re-suspend) before the POST resolves — tens of seconds, during
      // which this composer's owning HilChatWindow can legitimately re-render around it. React
      // 18 silently no-ops a state update dispatched from a stale closure onto an unmounted
      // owner, so calling these callbacks late is always safe; the previous mounted-guard here
      // instead actively dropped the pending->sent/failed transition on any such re-render,
      // leaving this echo's bubble stuck showing "Sending..." forever even after the backend
      // had already confirmed 200 OK. `onMessageStatusChange` targets this specific echoID, so
      // calling it after the fact is correct regardless of what else changed while it awaited.
      await submit(writer);
      const failed = useHilStore.getState().submitError !== null;
      onMessageStatusChange(echoID, failed ? 'failed' : 'sent');
    } else {
      onMessageStatusChange(echoID, 'sent');
      const next = presentations.find(p => p.Order > currentStep);
      if (next) onAdvance(next.Order);
    }
  };

  useImperativeHandle(ref, () => ({
    retry: (message: HilChatMessage) => {
      if (message.order !== currentStep) return;
      const primary = actions[0] ?? presetActions[0];
      if (primary) void send(primary, message.promptMessage ?? '', message.id);
    },
  }), [currentStep, actions, presetActions, step, currentFormData, currentUserID, reader, writer, nextEchoID, onAdvance, onMessageSent, onMessageStatusChange]);

  if (!reader || !writer || !step) return null;

  const handleActionClick = (action: HilPresentationAction) => void send(action, isChatStep ? draft : '');
  const handleChatSend = () => {
    const primary = actions[0] ?? presetActions[0];
    if (primary) void send(primary, draft);
  };

  return (
    <div className="hil-chat-composer">
      {error && <div className="hil-chat-composer__error">{error}</div>}
      {settings.visibility?.presetRow !== false && (
        <HilChatPresetRow
          presetActions={presetActions}
          behavior={settings.actionBar?.presetClickBehavior ?? 'send'}
          onSend={(action, text) => void send(action, text)}
          onFill={text => setDraft(text)}
          disabled={disabled}
        />
      )}
      {isChatStep && (
        <HilChatTextInput value={draft} onChange={setDraft} onSubmit={handleChatSend} disabled={disabled} />
      )}
      {settings.visibility?.actionBar !== false && (
        <HilActionBar
          actions={[...actions, ...(settings.actionBar?.defaultActions ?? [])]}
          onAction={handleActionClick}
          disabled={disabled}
        />
      )}
    </div>
  );
});
